import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
} from '@mui/material';
import { X } from 'lucide-react';
import DatePicker from 'components/common/datePicker/datePicker';
import { Site } from 'types/site';
import { PromoCode } from 'types/promoCode';
import { Alert } from 'components/alert/alert';

interface PromoCodeModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: any) => void;
  sites: Site[];
  theme?: 'light' | 'dark';
  isLoading?: boolean;
  initialData?: PromoCode | null;
  error?: string | null;
}

interface FormData {
  siteId: string | number;
  currencyCode: string;
  code: string;
  count: string | number;
  amount: string;
  minDeposit: string | number;
  endDate: string;
}

const getNextMonthDate = () => {
  const date = new Date();
  date.setMonth(date.getMonth() + 1);
  return date.toISOString();
};

const PromoCodeModal: React.FC<PromoCodeModalProps> = ({
  open,
  onClose,
  onSubmit,
  sites,
  theme = 'dark',
  isLoading = false,
  initialData = null,
  error = null,
}) => {
  const isDark = theme === 'dark';
  const isEdit = !!initialData;

  const [formData, setFormData] = useState<FormData>({
    siteId: '',
    currencyCode: 'USD',
    code: '',
    count: '',
    amount: '',
    minDeposit: '',
    endDate: getNextMonthDate(),
  });

  useEffect(() => {
    if (initialData) {
      setFormData({
        siteId: initialData.siteId || '',
        currencyCode: initialData.currencyCode || 'USD',
        code: initialData.code || '',
        count: initialData.count || '',
        amount: initialData.amount || '',
        minDeposit: initialData.minDeposit || '',
        endDate: initialData.endDate
          ? new Date(initialData.endDate).toISOString()
          : new Date().toISOString(),
      });
    } else {
      setFormData({
        siteId: '',
        currencyCode: 'USD',
        code: '',
        count: '',
        amount: '',
        minDeposit: '',
        endDate: getNextMonthDate(),
      });
    }
  }, [initialData, open]);

  const handleChange = (field: keyof FormData, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleNumericChange = (field: keyof FormData, value: string) => {
    if (/^\d*$/.test(value)) {
      handleChange(field, value);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Convert back to numbers for submission if necessary
    const submissionData = {
      ...formData,
      count: parseInt(formData.count.toString()) || 0,
      minDeposit: parseInt(formData.minDeposit.toString()) || 0,
    };
    onSubmit(submissionData);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          bgcolor: isDark ? '#1a2c38' : '#ffffff',
          color: isDark ? '#fff' : '#1a2c38',
          borderRadius: '12px',
          backgroundImage: 'none',
        },
      }}
    >
      <form onSubmit={handleSubmit}>
        <DialogTitle
          sx={{
            m: 0,
            p: 2,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderBottom: isDark ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid #e0e6ed',
          }}
        >
          {isEdit ? 'Edit Promo Code' : 'Create Promo Code'}
          <IconButton onClick={onClose} sx={{ color: isDark ? '#8698a6' : '#5d7182' }}>
            <X size={20} />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          {error && <Alert message={error} type="error" />}

          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid size={{ xs: 12 }}>
              <FormControl fullWidth size="small">
                <InputLabel sx={{ color: isDark ? '#8698a6' : '#5d7182' }}>Site</InputLabel>
                <Select
                  value={formData.siteId}
                  label="Site"
                  onChange={(e) => handleChange('siteId', e.target.value)}
                  required
                  sx={{
                    color: isDark ? '#fff' : '#1a2c38',
                    '.MuiOutlinedInput-notchedOutline': {
                      borderColor: isDark ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)',
                    },
                  }}
                >
                  {sites.map((site) => (
                    <MenuItem key={site.id} value={site.id}>
                      {site.title}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                size="small"
                label="Promo Code"
                value={formData.code}
                onChange={(e) => handleChange('code', e.target.value)}
                required
                InputLabelProps={{ sx: { color: isDark ? '#8698a6' : '#5d7182' } }}
                inputProps={{ sx: { color: isDark ? '#fff' : '#1a2c38' } }}
              />
            </Grid>

            <Grid size={{ xs: 6 }}>
              <TextField
                fullWidth
                size="small"
                label="Count (Max Uses)"
                placeholder="100"
                value={formData.count}
                onChange={(e) => handleNumericChange('count', e.target.value)}
                required
                InputLabelProps={{ sx: { color: isDark ? '#8698a6' : '#5d7182' } }}
                inputProps={{
                  sx: { color: isDark ? '#fff' : '#1a2c38' },
                  inputMode: 'numeric',
                  pattern: '[0-9]*',
                }}
              />
            </Grid>

            <Grid size={{ xs: 6 }}>
              <TextField
                fullWidth
                size="small"
                label="Amount"
                placeholder="50"
                value={formData.amount}
                onChange={(e) => handleNumericChange('amount', e.target.value)}
                required
                InputLabelProps={{ sx: { color: isDark ? '#8698a6' : '#5d7182' } }}
                inputProps={{
                  sx: { color: isDark ? '#fff' : '#1a2c38' },
                  inputMode: 'numeric',
                  pattern: '[0-9]*',
                }}
              />
            </Grid>

            <Grid size={{ xs: 6 }}>
              <TextField
                fullWidth
                size="small"
                label="Min Deposit"
                placeholder="1"
                value={formData.minDeposit}
                onChange={(e) => handleNumericChange('minDeposit', e.target.value)}
                required
                InputLabelProps={{ sx: { color: isDark ? '#8698a6' : '#5d7182' } }}
                inputProps={{
                  sx: { color: isDark ? '#fff' : '#1a2c38' },
                  inputMode: 'numeric',
                  pattern: '[0-9]*',
                }}
              />
            </Grid>

            <Grid size={{ xs: 6 }}>
              <FormControl fullWidth size="small">
                <InputLabel sx={{ color: isDark ? '#8698a6' : '#5d7182' }}>Currency</InputLabel>
                <Select
                  value={formData.currencyCode}
                  label="Currency"
                  onChange={(e) => handleChange('currencyCode', e.target.value)}
                  sx={{
                    color: isDark ? '#fff' : '#1a2c38',
                    '.MuiOutlinedInput-notchedOutline': {
                      borderColor: isDark ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)',
                    },
                  }}
                >
                  <MenuItem value="USD">USD</MenuItem>
                  <MenuItem value="EUR">EUR</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <DatePicker
                label="End Date"
                value={formData.endDate}
                onChange={(val) => handleChange('endDate', val)}
                disablePast
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions
          sx={{
            p: 2,
            borderTop: isDark ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid #e0e6ed',
          }}
        >
          <Button onClick={onClose} sx={{ color: isDark ? '#8698a6' : '#5d7182' }}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isLoading}
            sx={{ bgcolor: '#00b3f5', color: '#fff', '&:hover': { bgcolor: '#0090c4' } }}
          >
            {isLoading ? 'Processing...' : isEdit ? 'Save Changes' : 'Create'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default PromoCodeModal;
